import {
  ARENA_BOTTOM,
  ARENA_TOP,
  SCENE_W,
  THRONE_X,
  defaultConfig,
  densityScaleFor,
  hpForLevel,
  radiusForLevel,
  throneY,
  type GameConfig,
  type TeamId,
  type WeaponId,
} from '@rekrut/shared';
import { Rng } from './rng.js';
import { SpatialHash } from './spatialHash.js';
import {
  createTeam,
  other,
  type AnnounceId,
  type EndReason,
  type MatchState,
  type Participant,
  type Projectile,
  type QueueEntry,
  type Throne,
  type Unit,
} from './state.js';

/** Фиксированный шаг симуляции — 60 Гц (ТЗ §11). */
export const FIXED_DT = 1000 / 60;

/**
 * Симуляция осады. Ни одного импорта из pixi.js — движок обязан гоняться
 * в тестах и в реплеере записанных эфиров без браузера.
 */
export class Engine {
  cfg: GameConfig;
  state: MatchState;
  readonly rng: Rng;

  /**
   * Нужна картинка этого зрителя. Рендер по сигналу качает её заранее — ещё
   * до того, как боец окажется на поле. Иначе шар выходит с заглушкой и
   * меняет лицо уже на ходу.
   */
  onAvatarNeeded?: (userId: string, avatarUrl: string) => void;

  onMatchEnd?: (state: MatchState) => void;

  private acc = 0;
  private matchSeq = 0;
  /** Счёт по раундам — единственное, что переносится между матчами. */
  private readonly roundsWon: Record<TeamId, number> = { A: 0, B: 0 };
  private readonly hash = new SpatialHash<Unit>(128);
  private readonly scratch: Unit[] = [];
  private readonly scratch2: Unit[] = [];

  /** Антиабьюз (§12). */
  private readonly weaponLog = new Map<string, number[]>();
  private readonly lastCommentAt = new Map<string, number>();
  private readonly lastJoinAt = new Map<string, number>();
  private readonly queued = new Set<string>();
  private lastDeathTickerAt = 0;

  /**
   * Объявления, накопленные за текущий тик.
   *
   * Залп ракет убивает пятерых одновременно, и раньше на каждого выпускалось
   * своё объявление: 1-й молча, 2-й «Double», 3-й «Multi» и так далее. Диктор
   * успевал сказать только первое из них — то есть на пять трупов зритель
   * слышал «Double kill». Копим за тик и объявляем один раз по итоговой серии.
   */
  private readonly pendingAnnounce = new Map<
    string,
    { combo: number; kills: number; nickname: string; team: TeamId; first: boolean; king: boolean }
  >();

  /**
   * Отложенные волны залпа розы. Одна запись — один оставшийся «выстрел
   * очереди»; каждый тик выпускаются те, чьё время пришло.
   */
  private volleys: {
    userId: string;
    team: TeamId;
    x: number;
    y: number;
    left: number;
    nextAt: number;
  }[] = [];

  constructor(cfg: GameConfig = defaultConfig, seed?: number) {
    this.cfg = cfg;
    this.rng = new Rng(seed);
    this.state = this.freshState();
  }

  // ─── жизненный цикл матча ────────────────────────────────────────────────

  private freshState(): MatchState {
    const now = this.state?.now ?? 0;
    const t = this.cfg.teams;
    const mkThrone = (team: TeamId): Throne => ({
      team,
      x: THRONE_X,
      y: throneY(team, this.cfg.arena.throneOffsetY),
      hp: this.cfg.throne.hp,
      maxHp: this.cfg.throne.hp,
      shielded: true,
      defenders: 0,
      siegeSince: 0,
      hitFlash: 0,
      lastDamagedAt: 0,
    });

    const s: MatchState = {
      matchId: `m${++this.matchSeq}-${Date.now().toString(36)}`,
      phase: 'lobby',
      now,
      startedAt: now,
      phaseEndsAt: now + this.cfg.match.lobbySec * 1000,
      units: new Map(),
      unitList: [],
      projectiles: [],
      teams: {
        A: createTeam('A', t.A.label, t.A.color, t.A.colorDark),
        B: createTeam('B', t.B.label, t.B.color, t.B.colorDark),
      },
      thrones: { A: mkThrone('A'), B: mkThrone('B') },
      participants: new Map(),
      densityScale: 1,
      frontY: (ARENA_TOP + ARENA_BOTTOM) / 2,
      viewerCount: this.state?.viewerCount ?? 0,
      likeCounter: 0,
      likeThreshold: this.cfg.free.likeShieldBase,
      winner: null,
      endReason: null,
      rounds: { A: this.roundsWon.A, B: this.roundsWon.B },
      stats: {
        shotsFired: 0,
        kills: 0,
        entries: 0,
        weaponsUsed: { volley: 0, laser: 0, rocket: 0, barrage: 0, orbital: 0, purge: 0 },
        siegeSecondsA: 0,
        siegeSecondsB: 0,
      },
      fx: [],
      ticker: [],
    };

    // Пул снарядов создаётся один раз: никаких аллокаций в кадре (§11).
    for (let i = 0; i < this.cfg.unit.maxProjectiles; i++) {
      s.projectiles.push({
        active: false,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        team: 'A',
        damage: 0,
        ownerId: '',
        diesAt: 0,
        blastRadius: 0,
        throneDamage: 0,
        tint: 0,
        tx: 0,
        ty: 0,
        spiral: 0,
        spinPhase: 0,
        spinRate: 0,
      });
    }
    return s;
  }

  /**
   * Новый матч. Все, кто был на поле и в очереди, переносятся в очередь —
   * заставлять зрителя писать «А» заново после каждого матча значит терять
   * половину тех, кто уже играет.
   */
  restart(): void {
    const carry: QueueEntry[] = [];
    for (const u of this.state.unitList) {
      carry.push({
        userId: u.userId,
        nickname: u.nickname,
        avatarUrl: u.avatarUrl,
        team: u.team,
        queuedAt: this.state.now,
        readyAt: this.state.now,
        priority: false,
        castAt: -1e9,
        castDirX: 0,
        castDirY: 0,
      });
    }
    for (const tid of ['A', 'B'] as TeamId[]) carry.push(...this.state.teams[tid].queue);

    this.state = this.freshState();
    this.volleys.length = 0;
    this.pendingAnnounce.clear();
    this.queued.clear();
    this.weaponLog.clear();
    for (const e of carry) {
      if (this.queued.has(e.userId)) continue;
      this.queued.add(e.userId);
      e.readyAt = this.state.now;
      this.state.teams[e.team].queue.push(e);
    }
  }

  update(realDtMs: number, speedMul = 1): void {
    const scale = (this.state.phase === 'finale' ? this.cfg.match.finaleTimeScale : 1) * speedMul;
    this.acc += Math.min(realDtMs, 250) * scale;
    let guard = 0;
    while (this.acc >= FIXED_DT && guard++ < 12) {
      this.acc -= FIXED_DT;
      this.step(FIXED_DT);
    }
  }

  private step(dt: number): void {
    const s = this.state;
    s.now += dt;
    const dtSec = dt / 1000;

    this.updatePhase();
    if (s.phase === 'results') return;

    s.densityScale = densityScaleFor(this.cfg, s.unitList.length);

    this.updateVolleys();
    this.drainQueues(dtSec);
    this.rebuildHash();
    this.updateFront(dtSec);
    this.updateThrones(dtSec);
    this.updateUnits(dtSec);
    this.updateProjectiles(dtSec);

    this.flushAnnouncements();
    if (s.phase === 'battle') this.checkEnd();

    for (let i = s.ticker.length - 1; i >= 0; i--) {
      if (s.ticker[i]!.until <= s.now) s.ticker.splice(i, 1);
    }
  }

  private updatePhase(): void {
    const s = this.state;
    if (s.now < s.phaseEndsAt) return;

    switch (s.phase) {
      case 'lobby':
        s.phase = 'battle';
        s.startedAt = s.now;
        s.phaseEndsAt =
          this.cfg.match.maxDurationSec > 0
            ? s.now + this.cfg.match.maxDurationSec * 1000
            : Number.POSITIVE_INFINITY;
        this.banner('В АТАКУ', 'Пиши A или B в чат', 0xffffff, 1600);
        break;
      case 'battle':
        this.endMatch('timeout', this.leadingTeam());
        break;
      case 'finale':
        s.phase = 'results';
        s.phaseEndsAt = s.now + this.cfg.match.resultsSec * 1000;
        this.onMatchEnd?.(s);
        break;
      case 'results':
        this.restart();
        break;
    }
  }

  private endMatch(reason: EndReason, winner: TeamId | null): void {
    const s = this.state;
    if (s.phase !== 'battle') return;
    s.phase = 'finale';
    s.endReason = reason;
    s.winner = winner;
    s.phaseEndsAt = s.now + this.cfg.match.finaleSec * 1000;
    if (winner) {
      this.roundsWon[winner]++;
      s.rounds = { A: this.roundsWon.A, B: this.roundsWon.B };
    }
    const label = winner ? s.teams[winner].label : '—';
    this.banner(`${label} ВЗЯЛИ ТРОН`, REASON_TEXT[reason], winner ? s.teams[winner].color : 0xffffff, 2600);
    s.fx.push({ kind: 'screenshake', power: 18 });
  }

  private checkEnd(): void {
    for (const tid of ['A', 'B'] as TeamId[]) {
      if (this.state.thrones[tid].hp <= 0) {
        this.endMatch('throne', other(tid));
        return;
      }
    }
  }

  /** Кто ближе к победе — по остатку HP чужого трона. */
  leadingTeam(): TeamId | null {
    const a = this.state.thrones.B.hp;
    const b = this.state.thrones.A.hp;
    if (a === b) return null;
    return a < b ? 'A' : 'B';
  }

  // ─── очередь ─────────────────────────────────────────────────────────────

  countAlive(team: TeamId): number {
    let n = 0;
    for (const u of this.state.unitList) if (u.team === team) n++;
    return n;
  }

  /**
   * Боевая мощь стороны — сумма текущих HP всех её бойцов.
   *
   * Именно она, а не число голов, двигает линию фронта. Пока места на поле
   * есть, погибший заменяется мгновенно, поэтому по головам счёт всегда
   * ничейный и фронт стоит колом. Но погибший возвращается ПЕРВЫМ уровнем,
   * теряя накопленные сотни HP, — значит сторона, которая гибнет чаще,
   * реально слабеет, и это видно по движению линии.
   */
  teamPower(team: TeamId): number {
    let p = 0;
    for (const u of this.state.unitList) if (u.team === team) p += u.hp;
    return p;
  }

  /** Щит трона держит любой живой боец команды. */
  countDefenders(team: TeamId): number {
    return this.countAlive(team);
  }

  isQueued(userId: string): boolean {
    return this.queued.has(userId);
  }

  /** Встать в очередь. Один userId — одна запись. */
  enqueue(
    user: { userId: string; nickname: string; avatarUrl: string },
    team: TeamId,
    priority = false,
    delaySec = 0,
  ): boolean {
    const s = this.state;
    if (s.units.has(user.userId)) return false;
    const q = s.teams[team].queue;

    if (this.queued.has(user.userId)) {
      // Уже стоит: донат двигает его в голову очереди.
      if (!priority) return false;
      for (const tid of ['A', 'B'] as TeamId[]) {
        const list = s.teams[tid].queue;
        const i = list.findIndex((e) => e.userId === user.userId);
        if (i < 0) continue;
        const [entry] = list.splice(i, 1);
        entry!.priority = true;
        // Донат снимает и паузу после смерти — за это и платят.
        entry!.readyAt = s.now;
        s.teams[team].queue.unshift(entry!);
        return true;
      }
      return false;
    }

    if (q.length >= this.cfg.queue.maxQueued) return false;
    const entry: QueueEntry = {
      userId: user.userId,
      nickname: user.nickname,
      avatarUrl: user.avatarUrl,
      team,
      queuedAt: s.now,
      readyAt: priority ? s.now : s.now + delaySec * 1000,
      priority,
      castAt: -1e9,
      castDirX: 0,
      castDirY: 0,
    };
    this.queued.add(user.userId);
    if (priority) q.unshift(entry);
    else q.push(entry);
    return true;
  }

  /**
   * Впуск из очереди. Темп ограничен: сторона, которую только что выкосили,
   * не может мгновенно восстановить оборону — иначе осада невозможна.
   */
  private drainQueues(dtSec: number): void {
    const s = this.state;
    const cap = this.cfg.queue.maxAlive;
    // В лобби армии выходят целиком и разом: бой должен начинаться
    // столкновением, а не по капле.
    const gathering = s.phase === 'lobby';
    const rate = this.cfg.queue.entriesPerSec;

    for (const tid of ['A', 'B'] as TeamId[]) {
      const team = s.teams[tid];
      team.entryCredit = gathering || rate <= 0 ? cap : Math.min(team.entryCredit + rate * dtSec, 4);
    }

    // Потолок живых ОБЩИЙ, а не по половине на сторону.
    //
    // С раздельным потолком обе армии упираются в свои сто бойцов и стоят
    // 100 на 100 бесконечно: место погибшего мгновенно занимает следующий,
    // перевес не возникает никогда, фронт не двигается. Общий потолок делает
    // место на поле ресурсом: сторона, которая гибнет реже, занимает больше
    // мест, и это прямо выражается движением линии.
    let alive = s.unitList.length;
    while (alive < cap) {
      let best: { team: TeamId; index: number; readyAt: number; alive: number } | null = null;
      for (const tid of ['A', 'B'] as TeamId[]) {
        const team = s.teams[tid];
        if (team.entryCredit < 1) continue;
        for (let i = 0; i < team.queue.length; i++) {
          const e = team.queue[i]!;
          if (e.readyAt > s.now) continue;
          const cand = { team: tid, index: i, readyAt: e.readyAt, alive: this.countAlive(tid) };
          // Первым в бой идёт тот, кто ждёт дольше всех. При РАВНОМ времени —
          // сторона, которой на поле меньше.
          //
          // Без этого правила при старте раунда, когда вся очередь готова
          // одновременно, побеждало простое «A раньше B в цикле»: все места
          // на поле забирали красные, и синие выходили, только когда очередь
          // красных кончалась.
          const better =
            !best ||
            cand.readyAt < best.readyAt ||
            (cand.readyAt === best.readyAt && cand.alive < best.alive);
          if (better) best = cand;
          break;
        }
      }
      if (!best) break;

      const team = s.teams[best.team];
      team.entryCredit -= 1;
      const [e] = team.queue.splice(best.index, 1);
      this.queued.delete(e!.userId);
      if (this.spawnUnit(e!, best.team)) {
        alive++;
        s.stats.entries++;
      }
    }
  }

  // ─── бойцы ───────────────────────────────────────────────────────────────

  private spawnUnit(e: QueueEntry, team: TeamId): Unit | null {
    const s = this.state;
    if (s.units.has(e.userId)) return null;
    const cfg = this.cfg;
    const th = s.thrones[team];
    const maxHp = hpForLevel(cfg, 1);

    // Выход из-за трона: кольцо вокруг него.
    const angle = this.rng.range(0, Math.PI * 2);
    const dist = this.rng.range(cfg.arena.throneRadius + 34, cfg.arena.throneRadius + 150);

    const u: Unit = {
      userId: e.userId,
      nickname: e.nickname,
      avatarUrl: e.avatarUrl,
      team,
      x: clamp(th.x + Math.cos(angle) * dist, 40, SCENE_W - 40),
      y: clamp(th.y + Math.sin(angle) * dist * 0.6, ARENA_TOP + 30, ARENA_BOTTOM - 30),
      vx: 0,
      vy: 0,
      hp: maxHp,
      maxHp,
      radius: radiusForLevel(cfg, 1, s.densityScale),
      level: 1,
      kills: 0,
      invulnUntil: s.now + cfg.unit.spawnInvulnMs,
      shieldUntil: 0,
      crownUntil: 0,
      levelUpAt: 0,
      nextShotAt: s.now + this.rng.range(0, cfg.unit.shotIntervalMs),
      nextTargetAt: s.now + this.rng.range(0, cfg.unit.retargetMs),
      targetId: null,
      targetIsThrone: false,
      driftPhase: this.rng.range(0, Math.PI * 2),
      castAt: -1e9,
      castDirX: 0,
      castDirY: 0,
      kbX: 0,
      kbY: 0,
      lastKillAt: -1e9,
      combo: 0,
      spawnedAt: s.now,
      lastDamagedAt: 0,
      hitFlash: 0,
      showNameUntil: s.now + 1800,
    };

    s.units.set(u.userId, u);
    s.unitList.push(u);
    const p = this.participant(u.userId, u.nickname, u.avatarUrl);
    p.team = team;
    return u;
  }

  /**
   * Линия фронта и нейтральная полоса.
   *
   * Бойцы не пересекают полосу вокруг линии: они упираются в неё со своей
   * стороны и ведут огонь на расстоянии, вместо того чтобы перемешиваться в
   * свалку. Сама линия ползёт в сторону той команды, у которой меньше живых —
   * это и есть «давят друг друга», только выраженное положением фронта, а не
   * толканием тел.
   *
   * В счёт идут ВСЕ живые, включая запертых в своей зоне у трона. Иначе
   * прижатая к трону сторона не может отжать линию обратно, и любая осада
   * становится необратимой.
   */
  private updateFront(dtSec: number): void {
    const s = this.state;
    const cfg = this.cfg.arena;
    const a = this.teamPower('A');
    const b = this.teamPower('B');

    const lo = s.thrones.A.y + cfg.frontMargin;
    const hi = s.thrones.B.y - cfg.frontMargin;
    const raw = a + b === 0 ? 0.5 : a / (a + b);
    const shareA = clamp(0.5 + (raw - 0.5) * cfg.frontResponse, 0, 1);
    const target = lo + (hi - lo) * shareA;

    const step = cfg.frontSpeed * dtSec;
    const d = target - s.frontY;
    s.frontY += Math.abs(d) <= step ? d : Math.sign(d) * step;
    s.frontY = clamp(s.frontY, lo, hi);
  }

  /** Граница, за которую команде хода нет. */
  private frontEdge(team: TeamId, radius: number): number {
    const half = this.cfg.arena.bufferWidth / 2;
    return team === 'A' ? this.state.frontY - half - radius : this.state.frontY + half + radius;
  }

  /** Поставить очередь волн; первая уходит сразу же. */
  queueVolley(userId: string, team: TeamId, x: number, y: number, waves: number): void {
    this.volleys.push({ userId, team, x, y, left: waves, nextAt: this.state.now });
  }

  /**
   * Выпуск очередных волн. Точка пуска берётся у живого стрелка, если он ещё
   * на поле, — очередь идёт за ним следом, а не висит там, где он был.
   */
  private updateVolleys(): void {
    const s = this.state;
    for (let i = this.volleys.length - 1; i >= 0; i--) {
      const v = this.volleys[i]!;
      if (s.now < v.nextAt) continue;
      const live = s.units.get(v.userId);
      if (live) {
        v.x = live.x;
        v.y = live.y;
      }
      this.onVolleyWave?.(v.userId, v.team, v.x, v.y);
      v.left--;
      v.nextAt = s.now + this.cfg.weapons.volley.waveGapMs;
      if (v.left <= 0) this.volleys.splice(i, 1);
    }
  }

  /** Вызывается на каждую волну — сюда подвешен сам выстрел пуль. */
  onVolleyWave?: (userId: string, team: TeamId, x: number, y: number) => void;

  private rebuildHash(): void {
    this.hash.clear();
    for (const u of this.state.unitList) this.hash.insert(u);
  }

  private updateUnits(dtSec: number): void {
    const s = this.state;
    const cfg = this.cfg;

    for (let i = s.unitList.length - 1; i >= 0; i--) {
      const u = s.unitList[i]!;
      u.hitFlash = Math.max(0, u.hitFlash - dtSec * 3);
      u.radius = radiusForLevel(cfg, u.level, s.densityScale);

      // Боец идёт и дерётся с первой же секунды после выхода из очереди.
      // Никаких «подожди начала фазы»: зритель написал в чат, увидел себя на
      // поле — и его шар обязан сразу двинуться, даже если он на карте один.
      this.updateTargeting(u);
      this.march(u, dtSec);
      this.updateShooting(u);
    }

    this.resolveContacts(dtSec);
  }

  /**
   * Марш к чужому трону. Боец идёт ВСЕГДА и никогда не останавливается —
   * стреляет он на ходу, по тому, кто попался на пути.
   *
   * Раньше боец замирал, увидев врага в пределах дальности. На пустом поле в
   * начале матча колонны проходили насквозь друг через друга, после чего у
   * каждого бойца враг оказывался рядом — и всё войско вставало вперемешку,
   * никуда не двигаясь. Фронт должен держаться расталкиванием тел, а не
   * запретом на движение: тогда две стены давят друг друга, и та, за которой
   * больше народу, продавливает.
   */
  private march(u: Unit, dtSec: number): void {
    const s = this.state;
    const cfg = this.cfg;

    // Куда идти: по пути к трону цель — ближайший враг. Врага нет — идём
    // ломать трон.
    //
    // Раньше боец шёл строго на трон и проходил мимо противника; армии
    // просто менялись местами, и никакого фронта на экране не было. Стремясь
    // к ближайшему врагу, бойцы сами стягиваются в линию соприкосновения,
    // а расталкивание тел превращает её в стену, которая давит.
    const target = u.targetId ? s.units.get(u.targetId) : undefined;
    const enemyThrone = s.thrones[other(u.team)];
    const tx = target && !u.targetIsThrone ? target.x : enemyThrone.x;
    const ty = target && !u.targetIsThrone ? target.y : enemyThrone.y;

    const dx = tx - u.x;
    const dy = ty - u.y;
    const len = Math.hypot(dx, dy) || 1;
    u.vx = (dx / len) * cfg.unit.speed;
    u.vy = (dy / len) * cfg.unit.speed;

    // Боковое рыскание: колонна не должна быть линейкой.
    u.driftPhase += dtSec * 1.1;
    u.vx += Math.sin(u.driftPhase) * cfg.unit.driftSpeed;

    // Импульс отдачи складывается с маршем и быстро гаснет: шар реально
    // отлетает назад, а затем своим ходом возвращается вперёд. Смещать
    // только картинку нельзя — в конце анимации шар прыгал бы туда, куда
    // за это время ушёл оригинал.
    u.x += (u.vx + u.kbX) * dtSec;
    u.y += (u.vy + u.kbY) * dtSec;
    const damp = Math.exp(-dtSec / 0.13);
    u.kbX *= damp;
    u.kbY *= damp;
    if (Math.abs(u.kbX) < 1) u.kbX = 0;
    if (Math.abs(u.kbY) < 1) u.kbY = 0;

    // Границы арены.
    const r = u.radius;
    u.x = clamp(u.x, r, SCENE_W - r);
    u.y = clamp(u.y, ARENA_TOP + r, ARENA_BOTTOM - r);

    // Нейтральная полоса: за неё не заходят ни те, ни другие.
    const edge = this.frontEdge(u.team, r);
    if (u.team === 'A') {
      if (u.y > edge) u.y = edge;
    } else if (u.y < edge) {
      u.y = edge;
    }

    // В троны не входим — ни в свой, ни в чужой.
    for (const tid of ['A', 'B'] as TeamId[]) {
      const th = s.thrones[tid];
      const dx = u.x - th.x;
      const dy = u.y - th.y;
      const minD = cfg.arena.throneRadius + r;
      const d2 = dx * dx + dy * dy;
      if (d2 >= minD * minD || d2 === 0) continue;
      const d = Math.sqrt(d2);
      u.x = th.x + (dx / d) * minD;
      u.y = th.y + (dy / d) * minD;
    }
  }

  private resolveContacts(dtSec: number): void {
    const s = this.state;
    const force = this.cfg.unit.separationForce;
    const near = this.scratch;

    for (const u of s.unitList) {
      this.hash.query(u.x, u.y, u.radius * 2 + 30, near);
      for (const o of near) {
        if (o === u || o.userId < u.userId) continue;
        const dx = o.x - u.x;
        const dy = o.y - u.y;
        const minD = u.radius + o.radius;
        const d2 = dx * dx + dy * dy;
        if (d2 >= minD * minD || d2 === 0) continue;
        const d = Math.sqrt(d2);
        const push = ((minD - d) / minD) * force * dtSec;
        const nx = dx / d;
        const ny = dy / d;
        u.x -= nx * push;
        u.y -= ny * push;
        o.x += nx * push;
        o.y += ny * push;
      }
    }
  }

  private updateTargeting(u: Unit): void {
    const s = this.state;
    if (s.now < u.nextTargetAt) return;
    u.nextTargetAt = s.now + this.cfg.unit.retargetMs + this.rng.jitter(120);

    const enemy = other(u.team);
    const th = s.thrones[enemy];

    // Трон без щита в пределах дальности — приоритетная цель.
    const throneInRange =
      !th.shielded && Math.hypot(th.x - u.x, th.y - u.y) <= this.cfg.unit.range;
    if (throneInRange && this.rng.bool(this.cfg.unit.throneFocus)) {
      u.targetId = null;
      u.targetIsThrone = true;
      return;
    }

    const found = this.hash.query(u.x, u.y, this.cfg.unit.range, this.scratch2);
    let best: Unit | null = null;
    let bestD2 = Infinity;
    for (const o of found) {
      if (o.team !== enemy) continue;
      const d2 = (o.x - u.x) ** 2 + (o.y - u.y) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = o;
      }
    }
    if (best) {
      u.targetId = best.userId;
      u.targetIsThrone = false;
      return;
    }

    // Противников рядом нет, но трон открыт — идём к нему.
    if (!th.shielded) {
      u.targetId = null;
      u.targetIsThrone = true;
      return;
    }

    // Иначе идём вперёд, к ближайшему врагу вообще.
    let far: Unit | null = null;
    let farD2 = Infinity;
    for (const o of s.unitList) {
      if (o.team !== enemy) continue;
      const d2 = (o.x - u.x) ** 2 + (o.y - u.y) ** 2;
      if (d2 < farD2) {
        farD2 = d2;
        far = o;
      }
    }
    u.targetId = far?.userId ?? null;
    u.targetIsThrone = false;
  }

  private updateShooting(u: Unit): void {
    const s = this.state;
    const cfg = this.cfg;
    if (s.now < u.nextShotAt) return;
    let tx: number;
    let ty: number;
    if (u.targetIsThrone) {
      const th = s.thrones[other(u.team)];
      if (th.shielded) return;
      tx = th.x;
      ty = th.y;
    } else {
      const t = u.targetId ? s.units.get(u.targetId) : undefined;
      if (!t) return;
      const dist = Math.hypot(t.x - u.x, t.y - u.y);
      if (dist > cfg.unit.range) return;
      // Упреждение по текущей скорости цели.
      const lead = dist / cfg.unit.projectileSpeed;
      tx = t.x + t.vx * lead;
      ty = t.y + t.vy * lead;
    }

    u.nextShotAt = s.now + cfg.unit.shotIntervalMs + this.rng.jitter(cfg.unit.shotJitterMs);

    const ax = tx - u.x;
    const ay = ty - u.y;
    const al = Math.hypot(ax, ay) || 1;
    const p = this.acquireProjectile();
    if (!p) return;
    p.active = true;
    p.x = u.x + (ax / al) * u.radius;
    p.y = u.y + (ay / al) * u.radius;
    p.vx = (ax / al) * cfg.unit.projectileSpeed;
    p.vy = (ay / al) * cfg.unit.projectileSpeed;
    p.team = u.team;
    p.ownerId = u.userId;
    p.damage = cfg.unit.damage;
    p.blastRadius = 0;
    p.throneDamage = cfg.unit.damage;
    p.tint = 0;
    p.tx = tx;
    p.ty = ty;
    p.spiral = 0;
    p.spinPhase = 0;
    p.spinRate = 0;
    p.diesAt = s.now + cfg.unit.projectileTtlMs;
    // По приросту этого счётчика звук собирает выстрелы в залпы.
    s.stats.shotsFired++;
  }

  // ─── снаряды и урон ──────────────────────────────────────────────────────

  acquireProjectile(): Projectile | null {
    const list = this.state.projectiles;
    for (let i = 0; i < list.length; i++) if (!list[i]!.active) return list[i]!;
    return null;
  }

  private updateProjectiles(dtSec: number): void {
    const s = this.state;
    const near = this.scratch;

    for (const p of s.projectiles) {
      if (!p.active) continue;

      // Ракета не наводится: она летит в точку, где противник был на момент
      // пуска, и идёт туда по спирали — угол к цели гуляет вокруг прямого,
      // затухая у самой цели. Поэтому залп расходится веером и красиво
      // сходится, а не втыкается по прямой.
      if (p.spiral > 0) {
        const dx = p.tx - p.x;
        const dy = p.ty - p.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 26) {
          this.explode(p);
          p.active = false;
          continue;
        }
        p.spinPhase += p.spinRate * dtSec;
        const decay = clamp(dist / 420, 0, 1);
        const ang = Math.atan2(dy, dx) + Math.sin(p.spinPhase) * p.spiral * decay;
        const speed = Math.hypot(p.vx, p.vy) || 1;
        p.vx = Math.cos(ang) * speed;
        p.vy = Math.sin(ang) * speed;
      }

      const prevX = p.x;
      const prevY = p.y;
      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;

      if (
        s.now >= p.diesAt ||
        p.x < -80 ||
        p.x > SCENE_W + 80 ||
        p.y < ARENA_TOP - 80 ||
        p.y > ARENA_BOTTOM + 80
      ) {
        if (p.blastRadius > 0) this.explode(p);
        p.active = false;
        continue;
      }

      const enemy = other(p.team);
      const th = s.thrones[enemy];
      if (Math.hypot(th.x - p.x, th.y - p.y) < this.cfg.arena.throneRadius) {
        if (p.blastRadius > 0) this.explode(p);
        else this.damageThrone(enemy, p.throneDamage, p.ownerId);
        p.active = false;
        continue;
      }

      // Ищем попадание по ВСЕМУ отрезку, пройденному за кадр, а не по конечной
      // точке. Пуля бластера летит 1150 px/с, то есть 19 px за кадр, а боец
      // при высокой плотности мельчает до 12 px радиуса — проверка одной точки
      // пропускала бы попадания насквозь.
      const stepR = Math.hypot(p.x - prevX, p.y - prevY);
      this.hash.query(
        (p.x + prevX) / 2,
        (p.y + prevY) / 2,
        Math.max(70, p.blastRadius, stepR),
        near,
      );

      let victim: Unit | null = null;
      let bestT = Infinity;
      for (const u of near) {
        if (u.team === p.team || u.userId === p.ownerId) continue;
        const t = segParam(prevX, prevY, p.x, p.y, u.x, u.y);
        const hx = prevX + (p.x - prevX) * t;
        const hy = prevY + (p.y - prevY) * t;
        const dx = u.x - hx;
        const dy = u.y - hy;
        if (dx * dx + dy * dy > u.radius * u.radius) continue;
        // Из нескольких задетых берём того, кто встретился РАНЬШЕ по пути:
        // порядок ячеек хэша к дистанции отношения не имеет.
        if (t < bestT) {
          bestT = t;
          victim = u;
        }
      }
      if (!victim) continue;

      if (p.blastRadius > 0) this.explode(p);
      else this.damageUnit(victim, p.damage, s.units.get(p.ownerId) ?? null, true);

      p.active = false;
    }
  }

  private explode(p: Projectile): void {
    const s = this.state;
    const attacker = s.units.get(p.ownerId) ?? null;
    s.fx.push({ kind: 'explosion', x: p.x, y: p.y, radius: p.blastRadius });
    for (const u of s.unitList) {
      if (u.team === p.team) continue;
      const d = Math.hypot(u.x - p.x, u.y - p.y);
      if (d > p.blastRadius + u.radius) continue;
      this.damageUnit(u, p.damage, attacker, false);
    }
    const enemy = other(p.team);
    const th = s.thrones[enemy];
    if (Math.hypot(th.x - p.x, th.y - p.y) <= p.blastRadius + this.cfg.arena.throneRadius) {
      this.damageThrone(enemy, p.throneDamage, p.ownerId);
    }
  }

  /** Единственная точка нанесения урона бойцу. */
  damageUnit(target: Unit, amount: number, attacker: Unit | null, silent: boolean): void {
    const s = this.state;
    if (amount <= 0) return;
    if (s.now < target.invulnUntil) return;
    if (s.now < target.shieldUntil || s.now < s.teams[target.team].shieldUntil) {
      if (!silent) {
        s.fx.push({ kind: 'shock', x: target.x, y: target.y, radius: target.radius * 1.5, color: 0x9fe8ff });
      }
      return;
    }

    target.hp -= amount;
    target.lastDamagedAt = s.now;
    target.hitFlash = 1;
    if (!silent && amount >= 10) {
      s.fx.push({
        kind: 'damage',
        x: target.x,
        y: target.y - target.radius,
        amount: Math.round(amount),
        big: amount >= 50,
        color: 0xffffff,
      });
    }
    if (target.hp <= 0) this.kill(target, attacker);
  }

  /** Добивающий выстрел даёт уровень. Погибший теряет всё и уходит в очередь. */
  kill(u: Unit, attacker: Unit | null): void {
    const s = this.state;
    s.fx.push({ kind: 'death', x: u.x, y: u.y, color: s.teams[u.team].color });
    s.stats.kills++;
    s.teams[u.team].losses++;

    const victim = this.participant(u.userId, u.nickname, u.avatarUrl);
    victim.deaths++;

    if (attacker && attacker.team !== u.team && s.units.has(attacker.userId)) {
      attacker.kills++;
      s.teams[attacker.team].kills++;
      const ap = this.participant(attacker.userId, attacker.nickname, attacker.avatarUrl);
      ap.kills++;
      this.announceKill(attacker, u);
      this.levelUp(attacker);
    }

    const team = u.team;
    this.removeUnit(u);

    // Погибший возвращается сам, но не раньше чем через respawnSec. Писать
    // в чат заново не нужно — зритель уже в игре, он просто ждёт возрождения.
    this.enqueue(
      { userId: u.userId, nickname: u.nickname, avatarUrl: u.avatarUrl },
      team,
      false,
      this.cfg.queue.respawnSec,
    );

    if (this.rng.bool(0.12) || s.now - this.lastDeathTickerAt > 2500) {
      this.lastDeathTickerAt = s.now;
      if (attacker) {
        this.tickerPush(
          `@${attacker.nickname} убил @${u.nickname}`,
          s.teams[attacker.team].color,
          2200,
        );
      }
    }
  }

  /** Уровень даётся не за каждый кил: нужно набрать killsPerLevel добиваний. */
  /**
   * Голос диктора за серию убийств.
   *
   * Две независимые лестницы, как в Unreal Tournament: серия подряд в коротком
   * окне (double → multi → mega → ultra → monster) и общий счёт за одну жизнь
   * (rampage → dominating). За кил объявляем максимум одно — самое громкое,
   * иначе диктор говорит без остановки и перестаёт что-либо значить.
   */
  private announceKill(attacker: Unit, victim: Unit): void {
    const s = this.state;
    const c = this.cfg.announce;

    // Серия подряд: считается, пока паузы между килами короче окна.
    attacker.combo = s.now - attacker.lastKillAt <= c.comboWindowMs ? attacker.combo + 1 : 1;
    attacker.lastKillAt = s.now;

    const e = this.pendingAnnounce.get(attacker.userId) ?? {
      combo: attacker.combo,
      kills: attacker.kills,
      nickname: attacker.nickname,
      team: attacker.team,
      first: false,
      king: false,
    };
    e.combo = attacker.combo;
    e.kills = attacker.kills;
    if (s.stats.kills === 1) e.first = true;
    if (victim.level >= this.cfg.unit.maxLevel) e.king = true;
    this.pendingAnnounce.set(attacker.userId, e);
  }

  /**
   * Одно объявление на стрелка за тик — по ИТОГОВОЙ серии.
   *
   * Две независимые лестницы, как в Unreal Tournament: серия подряд в коротком
   * окне (double → multi → mega → ultra → monster) и общий счёт за одну жизнь
   * (rampage → dominating). Берётся самое громкое из подходящих.
   */
  private flushAnnouncements(): void {
    if (this.pendingAnnounce.size === 0) return;
    const s = this.state;
    const c = this.cfg.announce;

    for (const e of this.pendingAnnounce.values()) {
      let id: AnnounceId | null = null;
      if (e.first) id = 'first_blood';
      else if (e.combo >= 6) id = 'monster';
      else if (e.combo === 5) id = 'ultra';
      else if (e.combo === 4) id = 'mega';
      else if (e.combo === 3) id = 'multi';
      else if (e.combo === 2) id = 'double';
      else if (e.kills === c.dominatingAt) id = 'dominating';
      else if (e.kills === c.rampageAt) id = 'rampage';
      // Снять короля — отдельное событие: их на поле единицы.
      else if (e.king) id = 'headshot';
      if (!id) continue;

      s.fx.push({ kind: 'announce', id, nickname: e.nickname, color: s.teams[e.team].color });
    }
    this.pendingAnnounce.clear();
  }

  private levelUp(u: Unit): void {
    const cfg = this.cfg;
    if (u.level >= cfg.unit.maxLevel) return;
    if (u.kills % Math.max(1, cfg.levels.killsPerLevel) !== 0) return;
    u.level++;
    u.maxHp = hpForLevel(cfg, u.level);
    u.hp = Math.min(u.maxHp, u.hp + cfg.levels.healOnLevelUp);
    u.levelUpAt = this.state.now;
    u.showNameUntil = this.state.now + 2500;
    const p = this.participant(u.userId, u.nickname, u.avatarUrl);
    p.bestLevel = Math.max(p.bestLevel, u.level);

    this.state.fx.push({
      kind: 'levelup',
      x: u.x,
      y: u.y,
      level: u.level,
      color: this.state.teams[u.team].color,
    });
    if (u.level === cfg.unit.maxLevel) {
      this.banner('КОРОЛЬ', `@${u.nickname}`, 0xffd25e, 1800);
      this.tickerPush(`@${u.nickname} — КОРОЛЬ ${this.state.teams[u.team].label}`, 0xffd25e, 4000);
    }
  }

  removeUnit(u: Unit): void {
    const s = this.state;
    s.units.delete(u.userId);
    const i = s.unitList.indexOf(u);
    if (i >= 0) s.unitList.splice(i, 1);
  }

  // ─── троны ───────────────────────────────────────────────────────────────

  private updateThrones(dtSec: number): void {
    const s = this.state;
    const c = this.cfg.throne;
    const lo = s.thrones.A.y + this.cfg.arena.frontMargin;
    const hi = s.thrones.B.y - this.cfg.arena.frontMargin;

    for (const tid of ['A', 'B'] as TeamId[]) {
      const th = s.thrones[tid];
      th.hitFlash = Math.max(0, th.hitFlash - dtSec * 3);
      th.defenders = this.countAlive(tid);

      // Насколько линия прижата к этому трону. 0 — армия стоит у ворот.
      const pressure = tid === 'A' ? s.frontY - lo : hi - s.frontY;

      if (th.shielded) {
        if (pressure <= c.openGap) {
          th.shielded = false;
          th.siegeSince = s.now;
          this.banner('ТРОН ОТКРЫТ', `${s.teams[tid].label} продавлены`, s.teams[other(tid)].color, 1800);
          s.fx.push({ kind: 'screenshake', power: 8 });
        }
      } else {
        // Зазор для возврата больше зазора открытия: без гистерезиса щит
        // мигал бы на каждом дрожании линии.
        if (pressure >= c.restoreGap) {
          th.shielded = true;
          th.siegeSince = 0;
          this.tickerPush(`${s.teams[tid].label} отбросили врага`, s.teams[tid].color, 2500);
        }
        if (tid === 'A') s.stats.siegeSecondsA += dtSec;
        else s.stats.siegeSecondsB += dtSec;
      }

      if (th.hp > 0 && th.hp < th.maxHp) {
        th.hp = Math.min(th.maxHp, th.hp + c.regenPerSec * dtSec);
      }
    }
  }

  /** Урон трону. Пока щит держат защитники — трон неуязвим для всего. */
  damageThrone(team: TeamId, amount: number, byUserId: string): void {
    const s = this.state;
    const th = s.thrones[team];
    if (th.shielded || amount <= 0 || th.hp <= 0) return;

    th.hp = Math.max(0, th.hp - amount);
    th.hitFlash = 1;
    th.lastDamagedAt = s.now;

    const p = s.participants.get(byUserId);
    if (p) p.throneDamage += amount;
    if (amount >= 50) {
      s.fx.push({ kind: 'damage', x: th.x, y: th.y - 70, amount: Math.round(amount), big: true, color: 0xffd25e });
    }
  }

  // ─── помощники для экономики и оружия ────────────────────────────────────

  getUnit(userId: string): Unit | undefined {
    return this.state.units.get(userId);
  }

  unitsInRadius(x: number, y: number, r: number, team: TeamId | null, out: Unit[]): Unit[] {
    out.length = 0;
    for (const u of this.state.unitList) {
      if (team && u.team !== team) continue;
      if (Math.hypot(u.x - x, u.y - y) > r + u.radius) continue;
      out.push(u);
    }
    return out;
  }

  participant(userId: string, nickname: string, avatarUrl: string): Participant {
    const s = this.state;
    let p = s.participants.get(userId);
    if (!p) {
      p = {
        userId,
        nickname,
        avatarUrl,
        team: null,
        kills: 0,
        deaths: 0,
        bestLevel: 1,
        diamonds: 0,
        throneDamage: 0,
      };
      s.participants.set(userId, p);
      // Картинку заказываем в момент, когда человек написал букву, а не когда
      // шар уже вышел на поле: пока он стоит в очереди, аватарка успевает
      // доехать, и боец появляется сразу со своим лицом.
      if (avatarUrl) this.onAvatarNeeded?.(userId, avatarUrl);
    } else {
      p.nickname = nickname || p.nickname;
      if (avatarUrl && avatarUrl !== p.avatarUrl) {
        p.avatarUrl = avatarUrl;
        this.onAvatarNeeded?.(userId, avatarUrl);
      }
    }
    return p;
  }

  heal(u: Unit, amount: number): void {
    u.hp = Math.min(u.maxHp, u.hp + amount);
  }

  banner(title: string, subtitle: string, color: number, ms: number): void {
    this.state.fx.push({ kind: 'banner', title, subtitle, color, ms });
  }

  tickerPush(text: string, color: number, ms: number): void {
    const t = this.state.ticker;
    t.push({ text, color, until: this.state.now + ms });
    if (t.length > 6) t.splice(0, t.length - 6);
  }

  noteWeapon(id: WeaponId): void {
    this.state.stats.weaponsUsed[id]++;
  }

  canUseWeapon(userId: string): boolean {
    const w = this.cfg.weapons.perUserWindowMs;
    const now = this.state.now;
    const fresh = (this.weaponLog.get(userId) ?? []).filter((t) => now - t < w);
    this.weaponLog.set(userId, fresh);
    return fresh.length < this.cfg.weapons.perUserLimit;
  }

  noteWeaponUse(userId: string): void {
    const log = this.weaponLog.get(userId) ?? [];
    log.push(this.state.now);
    this.weaponLog.set(userId, log);
  }

  canComment(userId: string): boolean {
    return this.state.now - (this.lastCommentAt.get(userId) ?? -1e9) >= this.cfg.free.commentCooldownMs;
  }
  noteComment(userId: string): void {
    this.lastCommentAt.set(userId, this.state.now);
  }
  canJoin(userId: string): boolean {
    return this.state.now - (this.lastJoinAt.get(userId) ?? -1e9) >= 3000;
  }
  noteJoin(userId: string): void {
    this.lastJoinAt.set(userId, this.state.now);
  }
}

export const REASON_TEXT: Record<EndReason, string> = {
  throne: 'трон разрушен',
  timeout: 'по остатку трона',
  manual: 'матч остановлен',
};

/** Ближайшая к точке позиция на отрезке, в долях его длины 0..1. */
function segParam(x1: number, y1: number, x2: number, y2: number, px: number, py: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return 0;
  const t = ((px - x1) * dx + (py - y1) * dy) / len2;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
